import {selectConstructionPlanEntries} from "../../../../redux/reducers/privateStateReducer/selectors";
import {
  CONSTRUCTION_PLACEMENT_KINDS,
  resolveConstructionPlacement,
  TEXT_CELL_NOT_AVAILABLE_FOR_CONSTRUCTION
} from "../resolveConstructionPlacement";
import {validateCurrentPlayerPendingBuildings} from "../pendingBuildingsValidation";
import {buildConstructionPlanningViewState} from "./buildConstructionPlanningViewState";
import {getTokenConfig} from "../../../buildings/getTokenConfig";
import {TEXT_PLANNED_BUILDINGS_INVALID} from "../../../../constants/text/construction";

const getBuildingLabel = (token) => getTokenConfig(token)?.label || token;

// Причина размещения — первая строка: в тексте клика за ней идет подсказка,
// куда строить, в списке ошибок она лишняя
const getFirstLine = (message) => String(message).split('\n')[0];

/**
 * Проверка плана строительства перед тем, как доверить ему ход целиком
 * (чекбокс «Автоматически завершить ход»): встанет ли план в начале хода и
 * пройдет ли затем проверку завершения хода.
 *
 *  1. Размещение — ровно как применение плана (applyConstructionPlan): записи
 *     по порядку, каждая против поля с уже вставшими до нее, теми же проверками,
 *     что клик по полю (resolveConstructionPlacement). Не вставшая запись
 *     пропускается, и дальше план идет без нее — как и при применении.
 *     Элементы Элементалей — вытянутые при входе в планирование (они же станут
 *     жетонами хода), за вычетом потраченных уже вставшими записями.
 *  2. Расположение вставшего — та же проверка, что у кнопки завершения фазы
 *     (validatePendingBuildings): ферма без квартала рядом и т.п.
 *
 * Возвращает {isValid, invalidHexKeys, message} (message — null, если план
 * валиден; иначе заголовок и причины списком, как в тосте завершения фазы).
 * Пользователь не участник партии — проверять нечего, план валиден.
 */
export const validateConstructionPlan = (state) => {
  const entries = selectConstructionPlanEntries(state);
  const placedEntries = [];
  const invalidHexKeys = [];
  const errors = [];

  const addError = (hexKey, error) => {
    if (!invalidHexKeys.includes(hexKey)) {
      invalidHexKeys.push(hexKey);
    }
    if (!errors.includes(error)) {
      errors.push(error);
    }
  };

  for (const entry of entries) {
    // Вид строится по копии: кэш вида держит одну запись по ссылке на массив
    // записей, и растущий placedEntries подсунул бы ему устаревший вид
    const viewState = buildConstructionPlanningViewState(state, {entries: [...placedEntries], brushToken: entry.token});
    if (!viewState) {
      return {isValid: true, invalidHexKeys: [], message: null};
    }
    const placement = resolveConstructionPlacement(viewState, entry.hexKey);
    const isPlaced = placement.kind === CONSTRUCTION_PLACEMENT_KINDS.COMMON
      || placement.kind === CONSTRUCTION_PLACEMENT_KINDS.SPECIAL;
    if (!isPlaced) {
      const reason = getFirstLine(placement.errorMessage || TEXT_CELL_NOT_AVAILABLE_FOR_CONSTRUCTION);
      addError(entry.hexKey, `${getBuildingLabel(entry.token)}: ${reason}`);
    } else {
      placedEntries.push(entry);
    }
  }

  // Весь план встал — вид по тому же массиву, что у отрисовки: ее кэш остается
  // теплым
  const finalViewState = buildConstructionPlanningViewState(state, {
    entries: placedEntries.length === entries.length ? entries : placedEntries,
    brushToken: null,
  });
  if (finalViewState) {
    validateCurrentPlayerPendingBuildings(finalViewState).invalidBuildings.forEach((building) => {
      building.validationErrors.forEach((error) => addError(building.hexKey, error));
    });
  }

  if (errors.length === 0) {
    return {isValid: true, invalidHexKeys: [], message: null};
  }
  return {
    isValid: false,
    invalidHexKeys,
    message: [TEXT_PLANNED_BUILDINGS_INVALID, ...errors].join('\n• '),
  };
};
